import { useState, useEffect, useRef, useCallback } from 'react';
import { MessageSquare, Brain, Pencil, Compass } from 'lucide-react';
import { LandingPage, LandingCTA } from './LandingShell';

// ---------------------------------------------------------------------------
// Demo data
// ---------------------------------------------------------------------------

interface ClassificationLine {
  label: string;
  value: string;
  isCost?: boolean;
}

interface DemoTask {
  text: string;
  lines: ClassificationLine[];
}

const TASKS: DemoTask[] = [
  {
    text: 'Prepare Q2 board presentation for Friday',
    lines: [
      { label: 'Project', value: 'Work \u203A Presentations' },
      { label: 'Priority', value: 'High (deadline in 4 days)' },
      { label: 'Estimate', value: '3.5 hours' },
      { label: 'Tags', value: '#quarterly  #stakeholder-facing' },
      { label: 'Suggested', value: 'Block Thursday afternoon' },
      { label: 'cost:', value: '$0.0012', isCost: true },
    ],
  },
  {
    text: 'Buy groceries',
    lines: [
      { label: 'Project', value: 'Personal \u203A Errands' },
      { label: 'Priority', value: 'Low' },
      { label: 'Estimate', value: '45 min' },
      { label: 'Tags', value: '#recurring  #errand' },
      { label: 'cost:', value: '$0.0012', isCost: true },
    ],
  },
];

const TYPING_SPEED = 40; // ms per character
const LINE_DELAY = 200; // ms between classification lines

// ---------------------------------------------------------------------------
// State machine
// ---------------------------------------------------------------------------

type Phase =
  | 'typing1'
  | 'classifying1'
  | 'pause'
  | 'typing2'
  | 'classifying2'
  | 'done';

// ---------------------------------------------------------------------------
// DemoTerminal
// ---------------------------------------------------------------------------

function DemoTerminal() {
  const [phase, setPhase] = useState<Phase>('typing1');
  const [charIndex, setCharIndex] = useState(0);
  const [visibleLines, setVisibleLines] = useState(0);
  const [task2CharIndex, setTask2CharIndex] = useState(0);
  const [task2VisibleLines, setTask2VisibleLines] = useState(0);

  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  const schedule = useCallback((fn: () => void, ms: number) => {
    const id = setTimeout(fn, ms);
    timers.current.push(id);
    return id;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      timers.current.forEach(clearTimeout);
    };
  }, []);

  // Phase: typing1
  useEffect(() => {
    if (phase !== 'typing1') return;
    const text = TASKS[0]!.text;
    if (charIndex < text.length) {
      const id = schedule(() => setCharIndex((c) => c + 1), TYPING_SPEED);
      return () => clearTimeout(id);
    }
    // typing done — move to classifying
    schedule(() => setPhase('classifying1'), 300);
  }, [phase, charIndex, schedule]);

  // Phase: classifying1
  useEffect(() => {
    if (phase !== 'classifying1') return;
    const total = TASKS[0]!.lines.length;
    if (visibleLines < total) {
      const id = schedule(() => setVisibleLines((v) => v + 1), LINE_DELAY);
      return () => clearTimeout(id);
    }
    schedule(() => setPhase('pause'), 600);
  }, [phase, visibleLines, schedule]);

  // Phase: pause between tasks
  useEffect(() => {
    if (phase !== 'pause') return;
    schedule(() => setPhase('typing2'), 800);
  }, [phase, schedule]);

  // Phase: typing2
  useEffect(() => {
    if (phase !== 'typing2') return;
    const text = TASKS[1]!.text;
    if (task2CharIndex < text.length) {
      const id = schedule(
        () => setTask2CharIndex((c) => c + 1),
        TYPING_SPEED,
      );
      return () => clearTimeout(id);
    }
    schedule(() => setPhase('classifying2'), 300);
  }, [phase, task2CharIndex, schedule]);

  // Phase: classifying2
  useEffect(() => {
    if (phase !== 'classifying2') return;
    const total = TASKS[1]!.lines.length;
    if (task2VisibleLines < total) {
      const id = schedule(
        () => setTask2VisibleLines((v) => v + 1),
        LINE_DELAY,
      );
      return () => clearTimeout(id);
    }
    schedule(() => setPhase('done'), 400);
  }, [phase, task2VisibleLines, schedule]);

  const isTyping = phase === 'typing1' || phase === 'typing2';

  const cursor = (
    <span className="inline-block w-[0.55em] h-[1.1em] align-middle bg-primary-500 animate-pulse" />
  );

  return (
    <div className="rounded-2xl bg-surface-raised-dark border border-border-subtle-dark p-6 sm:p-10 font-mono text-sm sm:text-base leading-relaxed max-w-2xl mx-auto select-none">
      {/* Task 1 */}
      <div className="mb-6">
        <p className="text-text-primary-dark brightness-125">
          <span className="text-primary-500 mr-2">&gt;</span>
          {TASKS[0]!.text.slice(0, charIndex)}
          {phase === 'typing1' && cursor}
        </p>

        {visibleLines > 0 && (
          <div className="mt-3 ml-4 space-y-1">
            {TASKS[0]!.lines.slice(0, visibleLines).map((line) => (
              <ClassificationRow key={line.label} line={line} />
            ))}
          </div>
        )}
      </div>

      {/* Task 2 — only render once we've started typing it */}
      {(phase === 'typing2' ||
        phase === 'classifying2' ||
        phase === 'done') && (
        <div>
          <p className="text-text-primary-dark brightness-125">
            <span className="text-primary-500 mr-2">&gt;</span>
            {TASKS[1]!.text.slice(0, task2CharIndex)}
            {phase === 'typing2' && cursor}
          </p>

          {task2VisibleLines > 0 && (
            <div className="mt-3 ml-4 space-y-1">
              {TASKS[1]!.lines.slice(0, task2VisibleLines).map((line) => (
                <ClassificationRow key={line.label} line={line} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Subtle blinking cursor at the end when done */}
      {phase === 'done' && (
        <p className="mt-6 text-text-primary-dark brightness-125">
          <span className="text-primary-500 mr-2">&gt;</span>
          {cursor}
        </p>
      )}

      {/* Idle typing indicator during pause */}
      {phase === 'pause' && !isTyping && (
        <p className="text-text-primary-dark brightness-125">
          <span className="text-primary-500 mr-2">&gt;</span>
          {cursor}
        </p>
      )}
    </div>
  );
}

function ClassificationRow({ line }: { line: ClassificationLine }) {
  if (line.isCost) {
    return (
      <p className="text-primary-500 mt-2 text-xs sm:text-sm opacity-80">
        {line.label} {line.value}
      </p>
    );
  }

  return (
    <p>
      <span className="text-text-tertiary-dark font-mono inline-block w-24 sm:w-28 shrink-0">
        {line.label}
      </span>
      <span className="text-text-primary-dark">{line.value}</span>
    </p>
  );
}

// ---------------------------------------------------------------------------
// How-it-works steps
// ---------------------------------------------------------------------------

const STEPS: {
  icon: typeof MessageSquare;
  title: string;
  description: string;
}[] = [
  {
    icon: MessageSquare,
    title: 'Add tasks however you want',
    description: 'Type, voice, forward an email.',
  },
  {
    icon: Brain,
    title: 'Danny classifies automatically',
    description: 'Project, priority, effort, tags.',
  },
  {
    icon: Pencil,
    title: 'You review and adjust',
    description: 'Danny learns from your corrections.',
  },
  {
    icon: Compass,
    title: "Ask 'what should I do next?'",
    description: 'Get an actual answer, not a sorted list.',
  },
];

// ---------------------------------------------------------------------------
// LandingPageC
// ---------------------------------------------------------------------------

export function LandingPageC() {
  return (
    <LandingPage>
      {/* ── Hero ─────────────────────────────────────────────────── */}
      <section className="max-w-5xl mx-auto px-6 pt-24 pb-12 text-center">
        <span className="label-mono inline-block text-xs tracking-widest text-primary-500 border border-primary-500/40 rounded-full px-4 py-1 mb-6">
          see it work
        </span>

        <h1 className="text-4xl sm:text-5xl font-bold text-text-primary-light dark:text-text-primary-dark tracking-tight">
          Drop in a task. Watch Danny think.
        </h1>
      </section>

      {/* ── Demo ─────────────────────────────────────────────────── */}
      <section className="max-w-5xl mx-auto px-6 pb-24">
        <DemoTerminal />
      </section>

      {/* ── So What ──────────────────────────────────────────────── */}
      <section className="max-w-5xl mx-auto px-6 pb-24">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-3xl font-bold text-text-primary-light dark:text-text-primary-dark tracking-tight mb-4">
            That&rsquo;s what $2/month buys you.
          </h2>
          <p className="text-text-secondary-light dark:text-text-secondary-dark mb-4 leading-relaxed">
            50 classifications a month. Included. Every task you add, Danny
            reads it and figures out what it is, how long it&rsquo;ll take, and
            where it fits.
          </p>
          <p className="text-text-secondary-light dark:text-text-secondary-dark leading-relaxed">
            Your current app would store &ldquo;Prepare Q2 board presentation
            for Friday&rdquo; as a line of text and wait for{' '}
            <span className="font-semibold text-text-primary-light dark:text-text-primary-dark">
              YOU
            </span>{' '}
            to do all that thinking.
          </p>
        </div>
      </section>

      {/* ── How It Works ─────────────────────────────────────────── */}
      <section className="max-w-5xl mx-auto px-6 pb-24">
        <h2 className="text-3xl font-bold text-text-primary-light dark:text-text-primary-dark tracking-tight mb-10 text-center">
          Four steps. That&rsquo;s it.
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {STEPS.map((step, i) => {
            const Icon = step.icon;
            return (
              <div key={step.title} className="flex flex-col items-start">
                <div className="flex items-center gap-3 mb-3">
                  <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary-500/10 text-primary-500 font-mono text-sm font-bold">
                    {i + 1}
                  </span>
                  <Icon className="w-5 h-5 text-primary-500" />
                </div>
                <h3 className="font-semibold text-text-primary-light dark:text-text-primary-dark mb-1">
                  {step.title}
                </h3>
                <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                  {step.description}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Trust / Asimov ───────────────────────────────────────── */}
      <section className="max-w-5xl mx-auto px-6 pb-24">
        <div className="max-w-2xl mx-auto rounded-2xl bg-surface-raised-light dark:bg-surface-raised-dark border border-border-subtle-light dark:border-border-subtle-dark p-8 sm:p-10 border-l-4 border-l-primary-500">
          <p className="text-text-secondary-light dark:text-text-secondary-dark leading-relaxed mb-4">
            Danny doesn&rsquo;t rearrange your calendar. Doesn&rsquo;t
            auto-send emails. Doesn&rsquo;t touch anything you didn&rsquo;t ask
            it to touch.
          </p>
          <p className="text-text-tertiary-light dark:text-text-tertiary-dark leading-relaxed text-sm">
            Named after Daneel Olivaw. Asimov&rsquo;s robot who
            spent 20,000 years helping humanity by knowing when{' '}
            <em className="text-text-primary-light dark:text-text-primary-dark not-italic font-semibold">
              not
            </em>{' '}
            to act. That&rsquo;s the idea.
          </p>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────────── */}
      <LandingCTA
        headline="$2/month. You've seen what it does."
        subtitle="No setup. No commitment. Just try it."
      />
    </LandingPage>
  );
}
